import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { Paperclip } from "react-feather";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import type { SupportTicketAttachment } from "../supportTicketTypes";

/**
 * Props for {@link SupportTicketAttachmentsList}.
 */
interface SupportTicketAttachmentsListProps {
  /** Ticket-scoped attachments (not tied to a comment). */
  attachments: SupportTicketAttachment[];
}

/**
 * Lists a ticket's attachments beside the title as plain text links with a
 * paperclip icon. Opens each file in a new tab after resolving the download
 * URL from Firebase Storage.
 *
 * @param props - Ticket-scoped attachments.
 * @returns Attachments list, or null when there are none.
 */
const SupportTicketAttachmentsList = ({
  attachments,
}: SupportTicketAttachmentsListProps) => {
  if (!attachments.length) {
    return null;
  }

  const handleOpenAttachment = async (gcsPath: string) => {
    const url = await getFileDownloadUrl(gcsPath);
    if (!url) {
      toast.error("Unable to open the attachment.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <ul className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
      {attachments.map((attachment) => (
        <li key={attachment.id} className="min-w-0">
          <button
            type="button"
            onClick={() => {
              void handleOpenAttachment(attachment.gcsPath);
            }}
            className="inline-flex max-w-full items-center gap-1 text-xs font-normal text-tertiaryBlue-5 hover:underline"
            title={attachment.filename}
          >
            <Paperclip width="14" height="14" aria-hidden="true" />
            <span className="min-w-0 truncate">{attachment.filename}</span>
          </button>
        </li>
      ))}
    </ul>
  );
};

export default SupportTicketAttachmentsList;
